import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { HeartHandshake, Loader2, AlertCircle, CheckCircle2, Info } from 'lucide-react';
import { useToast } from '../components/ui/Toast';
import { validarDocumento, inferirTipoDocumento, type TipoDocumento } from '../utils/rutValidator';
import type { LocationSelection } from '../types/location';
import { validarTelefono } from '../utils/phone';
import { validarEmail } from '../utils/email';
import { validarFechaNacimiento, validarMontoEntero } from '../utils/validaciones';
import { MiembroFieldsIdentidad, type IdentidadValues } from '../components/miembro/MiembroFieldsIdentidad';
import { MiembroFieldsContacto, type ContactoValues } from '../components/miembro/MiembroFieldsContacto';
import { MiembroFieldsAptitudes } from '../components/miembro/MiembroFieldsAptitudes';
import { MiembroFieldsEmergencia } from '../components/miembro/MiembroFieldsEmergencia';
import { MiembroFieldsAportes } from '../components/miembro/MiembroFieldsAportes';
import { MiembroFotoUpload } from '../components/MiembroFotoUpload';
import { Checkbox } from '../components/ui/Checkbox';
import { ERROR_TEXT } from '../components/ui/tokens';
import { extraerBase64DeDataUri } from '../utils/dataUri';

type PageState = 'loading' | 'error' | 'form' | 'success' | 'success_compromiso' | 'solo_foto' | 'ya_enviado';

interface MiembroPublico {
  readonly id: string;
  readonly nombres: string;
  readonly apellidos: string;
  readonly genero?: string | null;
  readonly rut_pasaporte?: string;
  readonly fecha_nacimiento?: string;
  readonly direccion?: string;
  readonly comuna?: string;
  readonly ciudad?: string;
  readonly pais_residencia?: string;
  readonly pais_residencia_iso2?: string;
  readonly estado_residencia?: string;
  readonly estado_residencia_iso2?: string;
  readonly ciudad_residencia?: string;
  readonly ciudad_residencia_id?: string;
  readonly telefono?: string;
  readonly email?: string;
  readonly actividad_profesion?: string;
  readonly habilidad_talento?: string;
  readonly nombre_contacto_emergencia?: string;
  readonly numero_contacto_emergencia?: string;
  readonly aporte_mensual?: number;
  readonly aporte_cuota_iglesia?: number;
  readonly modo_pago_cuota_iglesia?: string;
  readonly foto_url?: string;
  readonly foto_compromiso_hasta?: string | null;
}

type FormData = {
  nombres: string; apellidos: string; genero: string; rut_pasaporte: string;
  fecha_nacimiento: string; direccion: string;
  telefono: string; email: string; email_confirm: string;
  actividad_profesion: string; habilidad_talento: string;
  nombre_contacto_emergencia: string; numero_contacto_emergencia: string;
  aporte_mensual: string; aporte_cuota_iglesia: string; modo_pago_cuota_iglesia: string;
};

const EMPTY_LOCATION: LocationSelection = { country: null, state: null, city: null };

// Largos máximos (espejo del límite que ya aplica el serverless, para avisar al instante).
const MAX_LEN: Partial<Record<keyof FormData, number>> = {
  nombres: 80, apellidos: 80, direccion: 200,
  actividad_profesion: 150, habilidad_talento: 150, nombre_contacto_emergencia: 100,
};

const EMPTY_FORM: FormData = {
  nombres: '', apellidos: '', genero: '', rut_pasaporte: '', fecha_nacimiento: '',
  direccion: '', telefono: '', email: '', email_confirm: '',
  actividad_profesion: '', habilidad_talento: '', nombre_contacto_emergencia: '',
  numero_contacto_emergencia: '', aporte_mensual: '', aporte_cuota_iglesia: '',
  modo_pago_cuota_iglesia: '',
};

const MODO_PAGO_OPTIONS_LINK = [
  { value: 'Mensual a JAL', label: 'Mensual a JAL' },
  { value: 'Mensual con PA', label: 'Mensual automático ★ Recomendado' },
  { value: 'Anual a JAL', label: 'Anual a JAL' },
  { value: 'Anual con PA', label: 'Anual automático' },
];

const STEPS = ['Identidad', 'Contacto', 'Perfil y aptitudes', 'Contacto de emergencia', 'Compromiso económico'] as const;

/**
 * Campos que avisan mientras se escriben, en vez de esperar al botón Siguiente. Antes solo lo hacían
 * el documento y el correo, así que un teléfono mal escrito, un año de cinco dígitos o un monto en
 * negativo se descubrían recién al intentar avanzar.
 */
const VALIDADORES_EN_VIVO: Partial<Record<keyof FormData, (v: string) => string | null>> = {
  telefono: (v) => validarTelefono(v, false),
  numero_contacto_emergencia: (v) => validarTelefono(v, false),
  email: (v) => validarEmail(v, false),
  fecha_nacimiento: validarFechaNacimiento,
  aporte_mensual: validarMontoEntero,
  aporte_cuota_iglesia: validarMontoEntero,
};

// La zona va fija: el plazo lo decide el servidor en Chile, así que un miembro que abra el link
// desde otro huso debe leer la misma fecha que el servidor va a honrar, no la de su reloj.
const formatearPlazo = (iso: string) =>
  new Date(iso).toLocaleDateString('es-CL', {
    timeZone: 'America/Santiago', weekday: 'long', day: 'numeric', month: 'long',
  });

// Campos que valida cada paso antes de avanzar (claves de error, incluye 'foto' y 'genero').
const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

const STEP_FIELDS: readonly string[][] = [
  ['nombres', 'apellidos', 'genero', 'rut_pasaporte', 'fecha_nacimiento', 'foto'],
  ['telefono', 'email', 'email_confirm', 'direccion', 'ubicacion'],
  ['actividad_profesion', 'habilidad_talento'],
  ['nombre_contacto_emergencia', 'numero_contacto_emergencia'],
  ['aporte_mensual', 'aporte_cuota_iglesia', 'modo_pago_cuota_iglesia'],
];

function obtenerErrorFoto(
  tieneFotoNueva: boolean,
  tieneFotoExistente: boolean,
  compromisoFoto: boolean,
  mostrarCompromiso: boolean,
): string | undefined {
  if (tieneFotoNueva || tieneFotoExistente || compromisoFoto) return undefined;
  return mostrarCompromiso ? 'Marca esta casilla para continuar sin fotografía.' : 'foto-pendiente';
}

interface EstadoPaginaProps {
  readonly state: Exclude<PageState, 'form'>;
  readonly errorMsg: string;
  readonly fotoCompromisoHasta: string | null;
  readonly onFotoReady: (dataUri: string) => void;
  readonly onFotoClear: () => void;
  readonly subiendoFoto: boolean;
  readonly errorSoloFoto: string;
  readonly puedeReintentarFoto: boolean;
  readonly onRetryFoto: () => void;
}

const EstadoPagina: React.FC<EstadoPaginaProps> = ({
  state,
  errorMsg,
  fotoCompromisoHasta,
  onFotoReady,
  onFotoClear,
  subiendoFoto,
  errorSoloFoto,
  puedeReintentarFoto,
  onRetryFoto,
}) => {
  if (state === 'loading') return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  if (state === 'error') return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="text-center max-w-sm">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-slate-700 font-medium mb-2">{errorMsg}</p>
        <p className="text-slate-500 text-sm">Contacta a tu administrador para obtener un nuevo link.</p>
      </div>
    </div>
  );

  if (state === 'ya_enviado') return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="text-center max-w-sm">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <p className="text-xl font-semibold text-slate-800 mb-2">Tu ficha ya fue recibida</p>
        <p className="text-slate-500 text-sm">Tu información fue enviada correctamente. El administrador la revisará pronto.</p>
      </div>
    </div>
  );

  if (state === 'success_compromiso') return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="text-center max-w-sm">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <p className="text-xl font-semibold text-slate-800 mb-2">¡Tus datos fueron guardados!</p>
        <p className="text-slate-700 text-sm mb-3">Falta tu fotografía.</p>
        <p className="text-slate-700 text-sm mb-4">
          <span className="font-semibold text-slate-900">Te comprometiste</span> a subirla{' '}
          <span className="font-semibold text-slate-900">
            {fotoCompromisoHasta ? `a más tardar el ${formatearPlazo(fotoCompromisoHasta)}` : 'dentro de los próximos 3 días'}
          </span>.
        </p>
        <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-3 text-left">
          <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-blue-900">
            Para subirla, vuelve a abrir el mismo link que recibiste por WhatsApp.
          </p>
        </div>
      </div>
    </div>
  );

  if (state === 'solo_foto') return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2">
        <HeartHandshake className="w-6 h-6 text-blue-600" />
        <span className="font-semibold text-slate-800">GestAd</span>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-8">
        <h2 className="text-base font-semibold text-slate-800 mb-2">Tu ficha ya fue recibida</h2>
        <p className="text-sm text-slate-600 mb-4">
          Solo falta tu fotografía
          {fotoCompromisoHasta ? `, y tienes plazo hasta el ${formatearPlazo(fotoCompromisoHasta)}` : ''}.
        </p>
        <MiembroFotoUpload onFotoReady={onFotoReady} onClear={onFotoClear} disabled={subiendoFoto} />
        {subiendoFoto && <p className="mt-3 text-sm text-slate-500">Guardando fotografía...</p>}
        {errorSoloFoto && (
          <div className="mt-3 space-y-2">
            <p className={ERROR_TEXT}>{errorSoloFoto}</p>
            {puedeReintentarFoto && (
              <button type="button" onClick={onRetryFoto}
                className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
                Reintentar subida
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <div className="text-center max-w-sm">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <p className="text-xl font-semibold text-slate-800 mb-2">¡Tus datos fueron guardados!</p>
        <p className="text-slate-500 text-sm">Gracias por completar tu ficha de inscripción.</p>
      </div>
    </div>
  );
};

export const RegistroMiembroPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { showToast } = useToast();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [fotoData, setFotoData] = useState<string | null>(null);
  const [existingFotoUrl, setExistingFotoUrl] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [tipoDoc, setTipoDoc] = useState<TipoDocumento>('rut');
  const [step, setStep] = useState(0);
  const [ubicacion, setUbicacion] = useState<LocationSelection>(EMPTY_LOCATION);
  const [compromisoFoto, setCompromisoFoto] = useState(false);
  const [mostrarCompromiso, setMostrarCompromiso] = useState(false);
  const [fotoCompromisoHasta, setFotoCompromisoHasta] = useState<string | null>(null);
  const [fotoSoloData, setFotoSoloData] = useState<string | null>(null);
  const [errorSoloFoto, setErrorSoloFoto] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`/api/invitacion?token=${token}`);
        const json = await res.json() as { data?: MiembroPublico; ya_enviado?: boolean; solo_foto?: boolean; error?: string };
        if (!res.ok) { setErrorMsg(json.error ?? 'Link no válido'); setPageState('error'); return; }
        const d = json.data!;
        if (json.solo_foto) {
          setExistingFotoUrl(null);
          setFotoCompromisoHasta(d.foto_compromiso_hasta ?? null);
          setPageState('solo_foto');
          return;
        }
        if (json.ya_enviado) { setPageState('ya_enviado'); return; }
        setFormData({
          nombres: d.nombres ?? '', apellidos: d.apellidos ?? '',
          genero: d.genero ?? '', rut_pasaporte: d.rut_pasaporte ?? '',
          fecha_nacimiento: d.fecha_nacimiento ?? '', direccion: d.direccion ?? '',
          telefono: d.telefono ?? '', email: d.email ?? '', email_confirm: d.email ?? '',
          actividad_profesion: d.actividad_profesion ?? '',
          habilidad_talento: d.habilidad_talento ?? '',
          nombre_contacto_emergencia: d.nombre_contacto_emergencia ?? '',
          numero_contacto_emergencia: d.numero_contacto_emergencia ?? '',
          aporte_mensual: d.aporte_mensual === undefined ? '' : String(d.aporte_mensual),
          aporte_cuota_iglesia: d.aporte_cuota_iglesia === undefined ? '' : String(d.aporte_cuota_iglesia),
          modo_pago_cuota_iglesia: d.modo_pago_cuota_iglesia ?? '',
        });
        setUbicacion({
          country: d.pais_residencia && d.pais_residencia_iso2
            ? { id: '', name: d.pais_residencia, iso2: d.pais_residencia_iso2 }
            : null,
          state: d.estado_residencia && d.estado_residencia_iso2
            ? { id: '', name: d.estado_residencia, iso2: d.estado_residencia_iso2 }
            : null,
          city: d.ciudad_residencia && d.ciudad_residencia_id
            ? { id: d.ciudad_residencia_id, name: d.ciudad_residencia }
            : null,
        });
        if (d.foto_url) setExistingFotoUrl(d.foto_url);
        setTipoDoc(inferirTipoDocumento(d.rut_pasaporte ?? ''));
        setPageState('form');
      } catch { setErrorMsg('Error de conexión.'); setPageState('error'); }
    };
    if (token) load();
  }, [token]);

  const computeErrors = (): Record<string, string> => {
    const e: Record<string, string> = {};
    const required: (keyof FormData)[] = [
      'nombres', 'apellidos', 'rut_pasaporte', 'fecha_nacimiento', 'direccion',
      'actividad_profesion', 'habilidad_talento',
      'nombre_contacto_emergencia',
      'aporte_mensual', 'aporte_cuota_iglesia', 'modo_pago_cuota_iglesia',
    ];
    for (const f of required) { if (!formData[f]?.trim()) e[f] = 'Campo requerido'; }
    for (const [f, max] of Object.entries(MAX_LEN)) {
      if (formData[f as keyof FormData].length > max) e[f] = `Máximo ${max} caracteres`;
    }
    if (!formData.genero) e.genero = 'Selecciona una opción';
    const fotoError = obtenerErrorFoto(!!fotoData, !!existingFotoUrl, compromisoFoto, mostrarCompromiso);
    if (fotoError) e.foto = fotoError;
    if (!ubicacion.country || !ubicacion.city) e.ubicacion = 'Selecciona país y ciudad';
    if (formData.email.trim() && !formData.email_confirm.trim()) {
      e.email_confirm = 'Confirma tu correo';
    } else if (formData.email.trim() && formData.email_confirm.trim() && formData.email !== formData.email_confirm) {
      e.email_confirm = 'Los correos no coinciden';
    }
    return { ...e, ...validarFormatos() };
  };

  // Validadores de formato por campo (RUT, teléfono, email, fecha, montos).
  const validarFormatos = (): Record<string, string> => {
    const e: Record<string, string> = {};
    const docError = validarDocumento(formData.rut_pasaporte, tipoDoc);
    if (docError) e.rut_pasaporte = docError;
    const telError = validarTelefono(formData.telefono, true);
    if (telError) e.telefono = telError;
    const emergError = validarTelefono(formData.numero_contacto_emergencia, true);
    if (emergError) e.numero_contacto_emergencia = emergError;
    const emailError = validarEmail(formData.email, true);
    if (emailError) e.email = emailError;
    const fechaError = validarFechaNacimiento(formData.fecha_nacimiento);
    if (fechaError) e.fecha_nacimiento = fechaError;
    const aporteError = validarMontoEntero(formData.aporte_mensual);
    if (aporteError) e.aporte_mensual = aporteError;
    const cuotaError = validarMontoEntero(formData.aporte_cuota_iglesia);
    if (cuotaError) e.aporte_cuota_iglesia = cuotaError;
    return e;
  };

  // Valida solo los campos del paso actual antes de avanzar.
  const validateStep = (s: number): boolean => {
    const all = computeErrors();
    const stepErrors: Record<string, string> = {};
    for (const k of STEP_FIELDS[s]) { if (all[k]) stepErrors[k] = all[k]; }
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) {
      if (step === 0 && !fotoData && !existingFotoUrl) setMostrarCompromiso(true);
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    scrollToTop();
  };

  const goBack = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
    scrollToTop();
  };

  const handleSubmit = async () => {
    const all = computeErrors();
    if (Object.keys(all).length > 0) {
      setErrors(all);
      const firstBad = STEP_FIELDS.findIndex((keys) => keys.some((k) => all[k]));
      if (firstBad >= 0) setStep(firstBad);
      scrollToTop();
      return;
    }
    setSending(true);
    try {
      const { email_confirm: _emailConfirm, ...datosMiembro } = formData;
      const payload = {
        ...datosMiembro,
        aporte_mensual: Number.parseInt(formData.aporte_mensual, 10),
        aporte_cuota_iglesia: Number.parseInt(formData.aporte_cuota_iglesia, 10),
        pais_residencia: ubicacion.country?.name,
        pais_residencia_iso2: ubicacion.country?.iso2,
        estado_residencia: ubicacion.state?.name,
        estado_residencia_iso2: ubicacion.state?.iso2,
        ciudad_residencia: ubicacion.city?.name,
        ciudad_residencia_id: ubicacion.city?.id,
        ciudad: ubicacion.city?.name,
        foto_data: fotoData,
        foto_compromiso: !fotoData && compromisoFoto,
        tipo_documento: tipoDoc,
      };
      const res = await fetch(`/api/invitacion?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json() as {
        data?: { foto_compromiso_hasta?: string | null };
        error?: string;
      };
      if (!res.ok) { showToast(json.error ?? 'Error al guardar.', 'error'); return; }
      if (!fotoData && compromisoFoto) {
        setFotoCompromisoHasta(json.data?.foto_compromiso_hasta ?? null);
      }
      setPageState(!fotoData && compromisoFoto ? 'success_compromiso' : 'success');
    } catch { showToast('Error de conexión.', 'error'); }
    finally { setSending(false); }
  };

  const handleTipoDocChange = (t: TipoDocumento) => {
    setTipoDoc(t);
    setErrors(prev => ({ ...prev, rut_pasaporte: validarDocumento(formData.rut_pasaporte, t) ?? '' }));
  };

  /**
   * Escribe el campo y, si se puede juzgar con lo tecleado hasta ahora, avisa de inmediato.
   * El documento va aparte porque su validación depende del tipo elegido (RUT o pasaporte).
   */
  const setValidando = (f: keyof FormData, v: string) => {
    setFormData(prev => ({ ...prev, [f]: v }));
    const validar = VALIDADORES_EN_VIVO[f];
    if (validar) setErrors(prev => ({ ...prev, [f]: validar(v) ?? '' }));
  };

  const onIdentidad = (f: keyof IdentidadValues, v: string) => {
    if (f === 'rut_pasaporte') {
      setFormData(prev => ({ ...prev, [f]: v }));
      setErrors(prev => ({ ...prev, rut_pasaporte: validarDocumento(v, tipoDoc) ?? '' }));
      return;
    }
    setValidando(f as keyof FormData, v);
  };

  const subirSoloFoto = async (base64: string) => {
    setSending(true);
    setErrorSoloFoto('');
    try {
      const res = await fetch(`/api/invitacion?token=${token}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ foto_data: base64 }),
      });
      const json = await res.json() as { error?: string };
      if (!res.ok) {
        setErrorSoloFoto(json.error ?? 'No pudimos guardar tu fotografía.');
        return;
      }
      setPageState('success');
    } catch { setErrorSoloFoto('Error de conexión. Intenta nuevamente.'); }
    finally { setSending(false); }
  };

  const prepararSubidaSoloFoto = (uri: string) => {
    const base64 = extraerBase64DeDataUri(uri);
    setFotoSoloData(base64);
    subirSoloFoto(base64);
  };

  if (pageState !== 'form') return (
    <EstadoPagina
      state={pageState}
      errorMsg={errorMsg}
      fotoCompromisoHasta={fotoCompromisoHasta}
      onFotoReady={prepararSubidaSoloFoto}
      onFotoClear={() => { setFotoSoloData(null); setErrorSoloFoto(''); }}
      subiendoFoto={sending}
      errorSoloFoto={errorSoloFoto}
      puedeReintentarFoto={!!fotoSoloData && !sending}
      onRetryFoto={() => { if (fotoSoloData) subirSoloFoto(fotoSoloData); }}
    />
  );

  const stepContent = [
    <MiembroFieldsIdentidad key="identidad"
      values={formData} onFieldChange={onIdentidad} errors={errors} required
      tipoDoc={tipoDoc} onTipoDocChange={handleTipoDocChange}
      onFotoReady={(uri) => { setFotoData(extraerBase64DeDataUri(uri)); }}
      onFotoClear={() => { setFotoData(null); setExistingFotoUrl(null); }}
      fotoError={mostrarCompromiso || errors.foto === 'foto-pendiente' ? undefined : errors.foto}
      fotoPosition="first" fotoUrl={existingFotoUrl ?? undefined}
      avisoCompromiso={mostrarCompromiso && !fotoData && !existingFotoUrl ? (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-3">
          <p className="text-sm text-amber-900">
            Tu ficha queda incompleta sin tu fotografía. Puedes continuar ahora y subirla después.
          </p>
          <div className="rounded-lg border border-amber-200 bg-white p-3">
            <Checkbox
              label="Me comprometo a subir mi fotografía dentro de los próximos 3 días."
              checked={compromisoFoto}
              onChange={setCompromisoFoto}
            />
          </div>
          {errors.foto && errors.foto !== 'foto-pendiente' && <p className={ERROR_TEXT}>{errors.foto}</p>}
        </div>
      ) : undefined} />,
    <MiembroFieldsContacto key="contacto"
      values={formData} onFieldChange={(f: keyof ContactoValues, v: string) => setValidando(f as keyof FormData, v)} errors={errors} required
      ubicacion={ubicacion} onUbicacionChange={setUbicacion} />,
    <MiembroFieldsAptitudes key="perfil"
      values={formData} onFieldChange={(f, v) => setValidando(f as keyof FormData, v)} errors={errors} required />,
    <MiembroFieldsEmergencia key="emergencia"
      values={formData} onFieldChange={(f, v) => setValidando(f as keyof FormData, v)} errors={errors} required />,
    <MiembroFieldsAportes key="economico"
      values={formData} onFieldChange={(f, v) => setValidando(f as keyof FormData, v)} errors={errors} required
      modoPagoOptions={MODO_PAGO_OPTIONS_LINK} />,
  ];

  const navButtons = (
    <>
      {step > 0 && (
        <button type="button" onClick={goBack} disabled={sending}
          className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 disabled:opacity-50 transition-colors">
          Atrás
        </button>
      )}
      {step < STEPS.length - 1 ? (
        <button type="button" onClick={goNext}
          className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors">
          Siguiente
        </button>
      ) : (
        <button type="button" onClick={handleSubmit} disabled={sending}
          className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
          {sending ? 'Guardando...' : 'Guardar mis datos'}
        </button>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-2">
        <HeartHandshake className="w-6 h-6 text-blue-600" />
        <span className="font-semibold text-slate-800">GestAd</span>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 pb-28 sm:pb-8">
        <div className="mb-6">
          <p className="text-sm font-medium text-slate-500 mb-2">Paso {step + 1} de {STEPS.length}</p>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden"
            role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={STEPS.length}>
            <div className="h-full bg-blue-600 rounded-full transition-all duration-300" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>

        <section key={step}>
          <h2 className="text-base font-semibold text-slate-800 mb-4">{STEPS[step]}</h2>
          {stepContent[step]}
        </section>

        <div className="hidden sm:flex gap-3 mt-8">{navButtons}</div>
      </main>

      <div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 flex gap-3">
        {navButtons}
      </div>
    </div>
  );
};
